// One file's upload (S1): prepare (the asset and the signed form), the post to the bucket, then
// complete. Each step's refusal surfaces as the API's error; the steps never repeat a paid or
// counted action, because prepare is idempotent under its key. The library's upload panel and the
// brand forms' image picker share it.
import { TransportError, UploadRefusedError, isApiError } from "./errors";
import { completeUpload, deleteAsset, prepareUpload } from "./generated/endpoints/assets/assets";
import type { Asset, AssetKind, MediaMime as Mime } from "./generated/model";
import { MediaMime, uploadRequestNameMax } from "./generated/zod";
import { sendToBucket } from "./upload";

// Display hints copied from S1's `MAX_BYTES` (backend `assets/contracts.py`), which the contract
// does not carry: the API decides with a 422, these only spare a doomed upload.
const MAX_BYTES: Record<AssetKind, number> = {
  image: 200_000_000,
  video: 2_000_000_000,
  audio: 200_000_000,
};

function kindOf(mime: Mime): AssetKind {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  return "audio";
}

/** Why a file cannot be uploaded at all, before any request, or `undefined`. */
export function refusalOf(file: File): string | undefined {
  const mime = MediaMime.safeParse(file.type);
  if (!mime.success) {
    return "This type of file cannot be uploaded. Use JPEG, PNG, WebP, MP4, MOV, WebM, MP3 or WAV.";
  }
  if (file.size === 0) return "This file is empty.";
  const kind = kindOf(mime.data);
  if (file.size > MAX_BYTES[kind]) {
    return `This file is over the ${MAX_BYTES[kind] / 1_000_000} MB limit for ${kind} files.`;
  }
  if (file.name.trim().length > uploadRequestNameMax) {
    return `Rename the file to ${uploadRequestNameMax} characters or fewer.`;
  }
  return undefined;
}

export interface UploadStep {
  step: "preparing" | "sending" | "completing";
  progress: number;
}

export interface UploadInput {
  teamId: string;
  brandId: string;
  file: File;
  /** One per attempt; a retry after a lost answer resends the same one. */
  key: string;
  /** The brand's folder the file lands in (spec upload-into-project); none for its top level. */
  folderId?: string | null | undefined;
  signal: AbortSignal;
  onStep: (step: UploadStep) => void;
}

/** Waits a moment and completes once more if the bucket's copy has not arrived yet. */
async function complete(input: UploadInput, assetId: string): Promise<Asset> {
  const { teamId, brandId, signal } = input;
  try {
    return await completeUpload(teamId, brandId, assetId, { signal });
  } catch (error) {
    if (!isApiError(error, "UPLOAD_NOT_RECEIVED")) throw error;
    await new Promise((resolve) => setTimeout(resolve, 1_000));
    return completeUpload(teamId, brandId, assetId, { signal });
  }
}

export async function uploadFile(input: UploadInput): Promise<Asset> {
  const { teamId, brandId, file, key, folderId, signal, onStep } = input;
  onStep({ step: "preparing", progress: 0 });
  const mime = MediaMime.parse(file.type);
  const folder = folderId === null || folderId === undefined ? {} : { folder_id: folderId };
  const ticket = await prepareUpload(
    teamId,
    brandId,
    { name: file.name.trim(), mime, bytes: file.size, ...folder },
    { "Idempotency-Key": key },
    { signal },
  );
  try {
    onStep({ step: "sending", progress: 0 });
    try {
      await sendToBucket(ticket, file, {
        signal,
        onProgress: (progress) => onStep({ step: "sending", progress }),
      });
    } catch (error) {
      // The bucket answered, and not with success: the file did not land, so the pending asset
      // only holds the file's name and a pending slot until the sweep (65 to 80 minutes). Delete
      // it, best effort, and say so with UploadRefusedError, which no retry resends a key for.
      // A post with no answer (`network`) may have landed: its asset stays for the same key.
      if (!(error instanceof TransportError) || error.kind !== "http") throw error;
      await deleteAsset(teamId, brandId, ticket.asset.id).catch(() => undefined);
      throw new UploadRefusedError(error.status, { cause: error });
    }
    onStep({ step: "completing", progress: 1 });
    return await complete(input, ticket.asset.id);
  } catch (error) {
    // Canceled after prepare: delete the pending asset, which releases its reserved bytes (S1),
    // rather than leaving it to the hourly sweep. Best effort: the sweep still catches a miss.
    if (signal.aborted) {
      await deleteAsset(teamId, brandId, ticket.asset.id).catch(() => undefined);
    }
    throw error;
  }
}
